export type DatePresetKey =
  | 'today' | 'tomorrow' | 'lastWeek' | 'thisWeek' | 'nextWeek' | 'currentMonth' | 'nextMonth';

export interface DateRange {
  from: Date;
  to: Date;
}

export const DATE_PRESET_KEYS: DatePresetKey[] =
  ['today', 'tomorrow', 'lastWeek', 'thisWeek', 'nextWeek', 'currentMonth', 'nextMonth'];

/** Fields that only ever hold past dates (created, updated, killed, last test ...). */
export const DATE_PRESETS_PAST: (DatePresetKey | 'custom')[] =
  ['today', 'thisWeek', 'lastWeek', 'currentMonth', 'custom'];

/** Fields that can hold future dates (due, planned execution, next test ...). */
export const DATE_PRESETS_FUTURE: (DatePresetKey | 'custom')[] = [...DATE_PRESET_KEYS, 'custom'];

/** Hunting date: the past presets plus the legacy hunting-year ranges kept for saved themes. */
export const DATE_PRESETS_HUNTING: string[] =
  [...DATE_PRESETS_PAST.filter(p => p !== 'custom'), 'last7', 'last30', 'currentJagdjahr', 'prevJagdjahr', 'custom'];

export const DATE_PRESET_LABEL_KEYS: Record<DatePresetKey, string> = {
  today:        'filter_date_range_items.today',
  tomorrow:     'filter_date_range_items.tomorrow',
  lastWeek:     'filter_date_range_items.last_week',
  thisWeek:     'filter_date_range_items.this_week',
  nextWeek:     'filter_date_range_items.next_week',
  currentMonth: 'filter_date_range_items.current_month',
  nextMonth:    'filter_date_range_items.next_month',
};

export function isDatePresetKey(value: string): value is DatePresetKey {
  return (DATE_PRESET_KEYS as string[]).includes(value);
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay   = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

/** Monday of the week containing `d`. */
function startOfWeek(d: Date): Date {
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return startOfDay(monday);
}

function endOfWeek(d: Date): Date {
  const sunday = new Date(startOfWeek(d));
  sunday.setDate(sunday.getDate() + 6);
  return endOfDay(sunday);
}

/** Cross-platform date-range presets, shared by the ag-grid filter component and MapTheme filter evaluation. */
export function rangeForDatePreset(preset: DatePresetKey): DateRange {
  const today = new Date();
  switch (preset) {
    case 'today':
      return { from: startOfDay(today), to: endOfDay(today) };
    case 'tomorrow': {
      const t = new Date(today); t.setDate(t.getDate() + 1);
      return { from: startOfDay(t), to: endOfDay(t) };
    }
    case 'lastWeek': {
      const t = new Date(today); t.setDate(t.getDate() - 7);
      return { from: startOfWeek(t), to: endOfWeek(t) };
    }
    case 'thisWeek':
      return { from: startOfWeek(today), to: endOfWeek(today) };
    case 'nextWeek': {
      const t = new Date(today); t.setDate(t.getDate() + 7);
      return { from: startOfWeek(t), to: endOfWeek(t) };
    }
    case 'currentMonth':
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to:   new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999),
      };
    case 'nextMonth':
      return {
        from: new Date(today.getFullYear(), today.getMonth() + 1, 1),
        to:   new Date(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999),
      };
  }
}
